import { CompanyHolidayResponse } from '@business/entities/company_holiday';
import { toMoment } from '@core/utils/time';
import { CalendarEvent } from './Calendar';

export const HOLIDAY_CALENDAR_TYPE = 'holiday';

/** The `item` carried by a holiday bar — lets click/drag handlers tell it apart from real events. */
export interface HolidayCalendarItem {
  type: typeof HOLIDAY_CALENDAR_TYPE;
  holiday: CompanyHolidayResponse;
  /** Never populated — present so shared renderers that read `item.users` don't need special cases. */
  users: never[];
}

export function isHolidayCalendarItem(item: unknown): item is HolidayCalendarItem {
  return !!item && (item as HolidayCalendarItem).type === HOLIDAY_CALENDAR_TYPE;
}

/**
 * Company holidays grey out their whole day cell(s) with the holiday's name — purely informational
 * (not clickable, not draggable) so users can see WHY a schedule skipped a day.
 */
export function holidayCalendarEvents(holidays: CompanyHolidayResponse[]): CalendarEvent<HolidayCalendarItem>[] {
  return holidays.map<CalendarEvent<HolidayCalendarItem>>(holiday => ({
    id: `holiday-${holiday.id}`,
    title: holiday.title,
    start: toMoment(holiday.startDate).startOf('day').unix(),
    // All-day ends are exclusive midnights to FullCalendar
    end: toMoment(holiday.endDate || holiday.startDate).startOf('day').add(1, 'day').unix(),
    allDay: true,
    editable: false,
    // Background display greys out the WHOLE day cell (visibly different from an event bar);
    // Calendar.tsx renders the name into it — FullCalendar shows no title on background events.
    display: 'background',
    backgroundColor: '#adb5bd',
    classNames: ['calendar-holiday', holiday.isNonWorking === false ? 'calendar-holiday-info' : 'calendar-holiday-blocking'],
    type: HOLIDAY_CALENDAR_TYPE,
    item: { type: HOLIDAY_CALENDAR_TYPE, holiday, users: [] as never[] },
  }));
}
